import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import dynamic from 'next/dynamic'
import { PurpleDot } from '@purple-dot/purple-dot-react'
import { openCart, showBanner } from '@store/redux/actions/otherActions'
import addToCart from '@store/redux/actions/addToCart'
import { checkStoreIs } from '@helpers/env'
import { scrollToTop } from '@helpers/utils'
import { createCartItem } from '@helpers/cart'
import {
  productViewedEvent,
  getProductCartItems,
  productSkuViewedEvent,
  combineVariantsInventoryLevel
} from '@helpers/products'
import getSelectedFilterOptions from '@helpers/products/getSelectedFilterOptions'
import { getVariantItem } from '@helpers/filters'
import { analytics } from '@helpers/analytics'
import { FilterState } from '@store/redux/reducers/filterReducer'
import { RootState } from '@store/redux/reducers'
import { ProductVariant, ProductWithVariants } from 'types/product'
import { CustomAttribute, CartLineItem } from 'types/checkout'
import { useCart } from '@components/cart/hooks'
import BackInStockModal from '@components/product/BackInStockModal'
import { SubmitButtonHandlers } from '@components/ui/Button/SubmitButton'
import { YotpoStarRatingProps } from './yotpoRatings/YotpoStarRatings'
import AddToCartButton from './AddToCartButton'
import Sezzle from './externalWidgets/Sezzle'
import ProductInfo from './ProductInfo'
import ProductHeader from './ProductHeader'
import Filters from './Filters'
import ProductSwatches from './ProductSwatches'
import ProductGallery from './ProductGallery'
import SweetenersList from './SweetenersList'
import StickyContainer from './AddToCartButton/StickyContainer'
import StickyAddToCart from './AddToCartButton/StickyAddToCart'
import useABtests from '@helpers/hooks/useABtests'
import styles from './styles.module.scss'
import MadeToOrderInfo from './MadeToOrderInfo'
import { MAX_MTO_VARIANT_QUANTITY, MTO_TAG } from '@consts/index'

const InjectPurpleDotButton = dynamic(() => import('./PurpleDotButton'))

const BROWSING_FIT_AB_TEST_ID = 'browsing-fit'

function lineItemsContainMatchingVariantSizes({
  lineItems,
  latestLineItemVariantId,
  latestLineItemCustomAttributes
}: {
  lineItems: CartLineItem[]
  latestLineItemVariantId: string | undefined
  latestLineItemCustomAttributes: CustomAttribute[]
}) {
  if (
    lineItems.length === 0 ||
    !latestLineItemVariantId ||
    latestLineItemCustomAttributes.length === 0
  ) {
    return false
  }

  const matchingLineItemSizeAttributes = lineItems.filter(line => {
    const cartItemVariantId = line.merchandise.id
    if (cartItemVariantId === latestLineItemVariantId) {
      const filteredLineAttributes = line.attributes.filter(
        attr => !['_addedToCartTimestamp', '_mto'].includes(attr.key)
      )
      return filteredLineAttributes.every(lineItemAttribute => {
        const variantAttribute = latestLineItemCustomAttributes.filter(
          customAttr => customAttr.key === lineItemAttribute.key
        )
        return variantAttribute[0].value === lineItemAttribute.value
      })
    }
  })

  return matchingLineItemSizeAttributes.length > 0
}

function getProductAvailability({ isPreOrder }: { isPreOrder: boolean }) {
  if (isPreOrder) {
    return 'preorder'
  }

  return 'in stock'
}

const checkIsPreorder = (variant: ProductVariant) =>
  variant.pre_order_inventory_quantity > 0

const checkSelectedVariantIsPreorder = (selectedVariant: ProductVariant[]) =>
  !!selectedVariant.length && selectedVariant.every(checkIsPreorder)

type ProductProps = {
  productColor: string
  selectedProduct: ProductWithVariants
  productHandle: string
  byline: string
  variantExist: boolean
} & YotpoStarRatingProps

function Product({
  productColor,
  productHandle,
  byline,
  variantExist,
  selectedProduct,
  starRatings
}: ProductProps) {
  const [openOOSModal, setOpenOOSModal] = useState(false)
  const toggleOOSModal = () => setOpenOOSModal(!openOOSModal)
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false)

  const { testsVariants } = useABtests()

  const isBrowsingFitAbTest =
    testsVariants[BROWSING_FIT_AB_TEST_ID] === 'variant-1'

  const { cart, updateCartQuantity } = useCart()

  const dispatch = useDispatch()
  const filterOptions = useSelector<RootState, FilterState['filterOptions']>(
    state => state.filters.filterOptions
  )

  const selectedFilterOptions = useMemo(
    () => getSelectedFilterOptions(selectedProduct.options, filterOptions),
    [selectedProduct.options, filterOptions]
  )

  const selectedOptionsString = selectedFilterOptions
    .map(option => option.value)
    .join(' / ')

  const selectedVariant = useMemo(() => {
    if (!variantExist) {
      return []
    }
    return getVariantItem(selectedProduct, selectedFilterOptions) || []
  }, [variantExist, selectedProduct, selectedFilterOptions])

  const productTags = selectedProduct?.tags.split(',')

  const hasProductVariants = !!selectedProduct.variants
  const hasAllVariantOptionsSelected =
    selectedProduct.options.length === selectedFilterOptions.length

  const emptyOptions = selectedProduct.options?.filter(
    productOption =>
      !selectedFilterOptions.find(
        filterOption => filterOption.type === productOption.type
      )
  )

  const selectedVariants = useMemo(
    () => getVariantItem(selectedProduct, selectedFilterOptions) || [],
    [selectedProduct, selectedFilterOptions]
  )

  const isSelectedProductOutOfStock = variantExist && !selectedVariants.length
  const isSelectedProductReturnable =
    !selectedProduct.tags.includes('non-returnable')

  const isActive =
    hasProductVariants &&
    selectedVariant.length > 0 &&
    hasAllVariantOptionsSelected &&
    (selectedVariant[0].inventory_quantity > 0 ||
      selectedVariant[0].pre_order_inventory_quantity > 0)

  const isPreOrder = checkSelectedVariantIsPreorder(selectedVariant)
  const isMTO = selectedProduct.tags.includes(MTO_TAG)

  useEffect(() => {
    if (variantExist) {
      productViewedEvent(selectedProduct)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct.id, variantExist])

  useEffect(() => {
    if (hasAllVariantOptionsSelected && selectedVariant.length) {
      const productOptionsAsObject = selectedFilterOptions.reduce(
        (result, option) => ({
          ...result,
          [option.type]: option.value
        }),
        {}
      )

      const { sku } =
        selectedVariant.find(variant =>
          isPreOrder ? variant : !checkIsPreorder(variant)
        ) || {}

      const productInventory = combineVariantsInventoryLevel(selectedVariant)

      productSkuViewedEvent(selectedProduct, {
        ...productOptionsAsObject,
        sku,
        variant: selectedOptionsString,
        availability: getProductAvailability({
          isPreOrder
        }),
        inventory: productInventory
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasAllVariantOptionsSelected,
    selectedProduct.id,
    selectedOptionsString,
    selectedVariant.length,
    variantExist
  ])

  useEffect(() => {
    if (isSelectedProductOutOfStock || openOOSModal) {
      analytics('Clicked out of stock')
    }
  }, [isSelectedProductOutOfStock, openOOSModal])

  const addItemToCart = useCallback(
    async ({
      triggerButtonSuccess,
      triggerButtonError
    }: SubmitButtonHandlers) => {
      try {
        const { variantId, customAttributes, lineItemInCart } = createCartItem(
          selectedProduct,
          selectedFilterOptions,
          cart.lines
        )

        const isMaxMtoQuantity =
          isMTO && lineItemInCart?.quantityInCart >= MAX_MTO_VARIANT_QUANTITY

        if (
          lineItemInCart?.quantityInCart > 0 &&
          lineItemInCart?.quantityInCart >= lineItemInCart?.quantityAvailable &&
          !isMTO
        ) {
          throw [{ message: 'Out of stock' }]
        } else if (isMaxMtoQuantity) {
          throw [{ message: 'Out of stock' }]
        }

        const lineItemsVariantSizesMatch = lineItemsContainMatchingVariantSizes(
          {
            lineItems: cart.lines,
            latestLineItemVariantId: lineItemInCart.lineItemVariantId,
            latestLineItemCustomAttributes: customAttributes
          }
        )

        if (
          lineItemInCart.lineItemId &&
          lineItemInCart.lineItemVariantId &&
          lineItemInCart.quantityInCart > 0 &&
          lineItemsVariantSizesMatch
        ) {
          await updateCartQuantity(
            lineItemInCart.lineItemId,
            lineItemInCart.lineItemVariantId,
            lineItemInCart.quantityInCart + 1,
            'Product Page'
          )
        } else {
          const cartItems = getProductCartItems({
            quantity: 1,
            variantId: `gid://shopify/ProductVariant/${variantId}`,
            productTags: selectedProduct.tags,
            customAttributes
          })
          await dispatch(addToCart(cartItems, 'Product Page'))
        }

        triggerButtonSuccess()

        dispatch(openCart())
      } catch (e) {
        const error = e as Error | { message: string }[]
        const errorType = Array.isArray(error) ? error[0]?.message : ''
        const errorsTypes = {
          'Variant is invalid': 'Add to basket - variant problem',
          'Out of stock': 'Add to basket - Out of stock'
        } as const
        const bannerType =
          errorsTypes[errorType as keyof typeof errorsTypes] ||
          'Add to basket - failed'

        dispatch(
          showBanner({
            isVisible: true,
            type: bannerType,
            theme: 'error'
          })
        )
        triggerButtonError()
        scrollToTop()
        setTimeout(() => {
          dispatch(
            showBanner({
              isVisible: false
            })
          )
        }, 10000)
      }
    },
    [selectedProduct, selectedFilterOptions, cart]
  )

  const showSezzleWidget = !!selectedProduct.price && checkStoreIs('us')

  if (!selectedProduct) return null

  return (
    <>
      <div data-testid="product-page" className={styles.productWrapper}>
        <div
          className={`${styles.productImages} ${
            isImageGalleryOpen ? styles.imageGalleryOpen : ''
          }`}
        >
          <ProductHeader
            product={selectedProduct}
            byline={byline}
            starRatings={starRatings}
            classes={styles.sidebar__productHeader}
          />
          <div className={styles.galleryWrapper}>
            <ProductGallery
              title={selectedProduct.title}
              images={selectedProduct.images}
              setIsImageGalleryOpen={setIsImageGalleryOpen}
            />
          </div>
        </div>
        <PurpleDot apiKey={process.env.PURPLE_DOT_APY_KEY}>
          <div className={styles.productForm__content}>
            <ProductHeader
              product={selectedProduct}
              byline={byline}
              starRatings={starRatings}
              classes={styles.sidebar__productHeader}
            />
            <div className={styles.product__swatchesContainer}>
              <ProductSwatches
                tags={productTags}
                selectedProduct={selectedProduct}
                selectedFilterOptions={selectedFilterOptions}
              />
            </div>
            <Filters
              product={selectedProduct}
              filterOptions={filterOptions}
              variantExist={variantExist}
              selectedOptions={selectedFilterOptions}
              isBrowsingFitAbTest={isBrowsingFitAbTest}
            />
            {isMTO && <MadeToOrderInfo classes={styles.productInfo__mto} />}
            {!isPreOrder && (
              <AddToCartButton
                active={isActive}
                handle={productHandle}
                onAddToCart={addItemToCart}
                onOutOfStock={toggleOOSModal}
                isSelectedProductOutOfStock={isSelectedProductOutOfStock}
              />
            )}
            {isPreOrder && !!selectedProduct.id && (
              <InjectPurpleDotButton
                isActive={isActive}
                selectedVariant={selectedVariant[0]}
                selectedOptions={selectedFilterOptions}
                product={selectedProduct}
                productColor={productColor}
              />
            )}
            {isSelectedProductReturnable && <SweetenersList />}
            {showSezzleWidget && !isPreOrder && (
              <Sezzle price={selectedProduct.price} />
            )}
            <ProductInfo
              text={selectedProduct.body_html}
              toggleOOSModal={toggleOOSModal}
              product={selectedProduct}
            />
          </div>
        </PurpleDot>
      </div>
      {openOOSModal && (
        <BackInStockModal
          onClose={toggleOOSModal}
          id={selectedProduct.id}
          options={selectedProduct.options}
          productType={selectedProduct.product_type}
          productFeed={selectedProduct.product_feed}
          title={selectedProduct.title}
          image={selectedProduct.image}
        />
      )}
      <StickyContainer
        isImageGalleryOpen={isImageGalleryOpen}
        hasEmptyOptions={!!emptyOptions.length}
        openOOSModal={openOOSModal}
      >
        <StickyAddToCart
          isSelectedProductOutOfStock={isSelectedProductOutOfStock}
          selectedProduct={selectedProduct}
          handle={productHandle}
          onOutOfStock={toggleOOSModal}
          onAddToCart={addItemToCart}
          productColor={productColor}
          hasAllVariantOptionsSelected={hasAllVariantOptionsSelected}
          emptyOptions={emptyOptions}
          active={isActive}
        />
      </StickyContainer>
    </>
  )
}

export default Product
